import React, { useCallback, useEffect, useState } from 'react';
import { pathOr, keys, omit } from 'ramda';
import { FormattedMessage } from 'react-intl';

import {
  Spinner,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import RecertificationApplicationForm from './RecertificationApplicationForm';
import { studentsTypes } from '../PrimaryLeaseApplication/AffordablePrimaryForm/configuration';
import { AFFORDABLE_PROGRAMS } from '../../constants/affordableProgramNames';
import {
  INITIAL_CERT_TYPE,
  INITIAL_UT_CERT_TYPE,
} from '../AffordableQualificationTab/HUDTab/constants';
import AffordableQualificationService from '../../services/affordableQualificationService';
import { getSettingsByPropertyAndProgram } from '../GeneralAffordable/utils';
import { TAB_KEYS } from '../ResidentProfile/constants';
import { getURLProgramName } from '../../utils/affordable';
import messages from './messages';
import {
  getQualificationFormDisabledState,
  getCertTabFromLocation,
  buildCertNavSearch,
} from '../QualificationForms/utils';
import { shouldUseHudCompletedTabFormReads } from '../GeneralAffordable/isHudCompletedTab';
import usePermissionsHook from '../../components/ElementWithPermissions/usePermissionsHook';
import store from '../../store';

const RecertificationApplicationV2 = (props) => {
  const {
    actions,
    intl,
    history,
    change,
    selectedProperty,
    employmentStatuses,
    states,
    recertificationFormData,
    residentId,
    affordableQualificationId,
    applicantId,
    applicationId,
    householdId,
    programName,
    flags,
    location,
  } = props;

  const [affordableQualification, setAffordableQualification] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const hasModifyReopenCertPermission = usePermissionsHook({
    scope: ['hud-qualification-modify-reopen-cert'],
  });

  const getIsInitialCertForNonHUD = useCallback((): boolean => {
    const { affordableQualificationCertificationType } =
      recertificationFormData;
    const formattedSetup = getSettingsByPropertyAndProgram(
      selectedProperty?.setup || {},
      programName,
    );
    const requiresInitialCertificationForNonHUD =
      formattedSetup.requiresInitialCertification ?? false;
    const requiresLIHTCInitialCertFlag =
      (flags?.initialCertsForLihtc ?? false) &&
      requiresInitialCertificationForNonHUD;
    const isNonHUDProgram = programName !== AFFORDABLE_PROGRAMS.HUD;
    return (
      [INITIAL_CERT_TYPE, INITIAL_UT_CERT_TYPE].includes(
        affordableQualificationCertificationType,
      ) &&
      isNonHUDProgram &&
      requiresLIHTCInitialCertFlag
    );
  }, [
    flags?.initialCertsForLihtc,
    programName,
    recertificationFormData,
    selectedProperty?.setup,
  ]);

  const fetchAffordableQualification = useCallback(
    async (organizationId, propertyId, qualificationId) => {
      try {
        const aqService = new AffordableQualificationService();
        const affordableQual = await aqService.getById(
          organizationId,
          propertyId,
          qualificationId,
        );
        setAffordableQualification(affordableQual);
        setIsLoading(false);
        setIsError(false);
      } catch (error) {
        setAffordableQualification({});
        setIsLoading(false);
        setIsError(true);
      }
    },
    [],
  );

  const locationSearch = location?.search ?? '';
  const useHudCompletedTabSnapshot = shouldUseHudCompletedTabFormReads({
    flags,
    programName,
    certTab: getCertTabFromLocation(location),
  });

  useEffect(() => {
    actions.getAllStates();
    actions.getAllEmploymentStatuses();
    actions.getRecertificationFormInformation(
      applicantId,
      affordableQualificationId,
      {
        isHudCompletedTab: useHudCompletedTabSnapshot,
        affordableQualificationId,
      },
    );
    fetchAffordableQualification(
      selectedProperty?.organizationId,
      selectedProperty?.id,
      affordableQualificationId,
    );

    return () => {
      actions.cleanRecertificationFormInformation();
    };
  }, [
    actions,
    applicantId,
    affordableQualificationId,
    fetchAffordableQualification,
    selectedProperty?.id,
    selectedProperty?.organizationId,
    locationSearch,
    programName,
    useHudCompletedTabSnapshot,
  ]);

  const handleCancel = useCallback(() => {
    const urlProgramName = programName
      ? getURLProgramName(programName)
      : TAB_KEYS.HUD;
    const path = `/resident/${residentId}/?tab=${urlProgramName}${buildCertNavSearch(
      location,
    )}`;
    const selectedPropertyId = store.getState()?.app?.selectedProperty?.id;
    const urlToNavigateTo = selectedPropertyId
      ? `/property/${selectedPropertyId}${path}`
      : path;
    history.push(urlToNavigateTo);
  }, [history, location, programName, residentId]);

  const handleSubmit = useCallback(
    (formData: Object) => {
      const massageEmployerInfo = (employerInfo: Object): Object => {
        const { phoneNumber, address } = employerInfo;
        let employerAddress;
        if (address) {
          employerAddress = {
            employerStreetAddress: address.street,
            ...omit(['street'], address),
          };
        }
        const employerObject = omit(['phoneNumber', 'address'], employerInfo);
        const setNulls = (obj: Object) => (withNulls, key) =>
          obj[key] === ''
            ? { ...withNulls, [key]: null }
            : { ...withNulls, [key]: obj[key] };
        const finalEmployerObject = keys(employerObject).reduce(
          setNulls(employerObject),
          {},
        );
        const finalEmployerAddress = employerAddress
          ? keys(employerAddress).reduce(setNulls(employerAddress), {})
          : null;
        return {
          ...finalEmployerObject,
          ...finalEmployerAddress,
          employerPhone: phoneNumber,
        };
      };

      const {
        applicantsCurrentEmployer,
        applicantsSecondCurrentEmployer,
        applicantsPreviousEmployer,
        studentInformation,
        employmentInformation,
        hasSignedCopy,
        isComplete,
      } = formData;

      const isInitialCertForNonHUD = getIsInitialCertForNonHUD();

      const yesNoDict = { yes: true, no: false };
      const changeHousehold = pathOr(
        null,
        ['changeHouseholdInformation'],
        formData,
      );
      const anticipateChangeHouseholdString = pathOr(
        null,
        ['changeHouseholdInformation', 'anticipateChangeHousehold'],
        formData,
      );
      const anticipateChangeHousehold = anticipateChangeHouseholdString
        ? yesNoDict[anticipateChangeHouseholdString]
        : null;
      const changeHouseholdInformation = changeHousehold
        ? {
            ...changeHousehold,
            anticipateChangeHousehold,
          }
        : null;

      const currentEmployer = massageEmployerInfo(applicantsCurrentEmployer);
      const secondCurrentEmployer = massageEmployerInfo(
        applicantsSecondCurrentEmployer,
      );
      const previousEmployer = massageEmployerInfo(applicantsPreviousEmployer);
      const employmentStatusId = employmentInformation
        ? employmentInformation.status
        : null;

      const anticipateChangeEmployerString = pathOr(
        null,
        ['anticipateChangeEmployer'],
        employmentInformation,
      );
      const anticipateChangeEmployer = anticipateChangeEmployerString
        ? yesNoDict[anticipateChangeEmployerString]
        : null;

      const recertFormData = {
        applicantsCurrentEmployer: currentEmployer,
        applicantsSecondCurrentEmployer: secondCurrentEmployer,
        applicantsPreviousEmployer: previousEmployer,
        employmentStatusId,
        studentInformation,
        changeHouseholdInformation,
        hasSignedCopy,
        anticipateChangeEmployer,
        isComplete,
        isInitialCertForNonHUD,
      };
      actions.saveRecertificationForm(
        recertFormData,
        applicantId,
        residentId,
        applicationId,
        programName,
        affordableQualificationId,
      );
    },
    [
      actions,
      affordableQualificationId,
      applicantId,
      applicationId,
      getIsInitialCertForNonHUD,
      programName,
      residentId,
    ],
  );

  const handlePrintApplicationForm = useCallback(() => {
    actions.printRecertificationApplicationForm(
      selectedProperty?.organizationId,
      selectedProperty?.id,
      applicantId,
      affordableQualificationId,
      programName,
      selectedProperty?.name,
    );
  }, [
    actions,
    affordableQualificationId,
    applicantId,
    programName,
    selectedProperty,
  ]);

  const isComplete = pathOr('', ['isComplete'], recertificationFormData);
  const propertyName = pathOr('', ['name'], selectedProperty);
  const isInitialCertForNonHUD = getIsInitialCertForNonHUD();
  const certTab = getCertTabFromLocation(location);
  const { isDisabled: isFormDisabled, isDisabledByPermission } =
    getQualificationFormDisabledState({
      affordableQualification,
      flags,
      programName,
      certTab,
      hasModifyReopenCertPermission,
    });

  if (isError) {
    return (
      <Grid container align="center" justify="center">
        <Grid item xs={12}>
          <Typography sx={{ fontSize: '12px' }}>
            <FormattedMessage {...messages.loadError} />
            <button
              onClick={() => {
                fetchAffordableQualification(
                  selectedProperty?.organizationId,
                  selectedProperty?.id,
                  affordableQualificationId,
                );
              }}
            >
              <FormattedMessage {...messages.retry} />
            </button>
          </Typography>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return <Spinner />;
  }

  const noPermissionTooltipTitle = isDisabledByPermission
    ? intl.formatMessage(messages.noPermissionAction)
    : '';

  return (
    <Tooltip title={noPermissionTooltipTitle} followCursor>
      <div>
        <RecertificationApplicationForm
          intl={intl}
          change={change}
          applicantId={applicantId}
          householdId={householdId}
          employmentStatuses={employmentStatuses}
          states={states}
          propertyName={propertyName}
          handleCancel={handleCancel}
          onSubmit={handleSubmit}
          studentsTypes={studentsTypes}
          initialValues={recertificationFormData}
          isComplete={isComplete}
          handlePrintRecertApplicationForm={handlePrintApplicationForm}
          isInitialCertForNonHUD={isInitialCertForNonHUD}
          isFormDisabled={isFormDisabled}
        />
      </div>
    </Tooltip>
  );
};

export default RecertificationApplicationV2;
